import React, { Component } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { useDrag, useDrop } from "react-dnd";
import FamilyService from "../services/family.service";
import AuthService from "../services/auth.service";

// Function to detect touch devices and return appropriate backend
const getBackend = () => {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  return isTouchDevice ? TouchBackend : HTML5Backend;
};

// Context Menu Component
const ContextMenu = ({ item, sourceColumn, position, onSelect, onClose, onDelete, onTypeSelect }) => {
  const columns = [
    { key: 'items', label: 'Grocery Items' },
    { key: 'ametller', label: 'Ametller' },
    { key: 'bonpreu', label: 'BonPreu' },
    { key: 'others', label: 'Others' }
  ];

  const types = [
    { key: null, label: 'No Type', color: '#007bff' },
    { key: 'sg', label: 'SG', color: '#ff9800' },
    { key: 'vegetables', label: 'Vegetables', color: '#4caf50' },
    { key: 'meat', label: 'Meat', color: '#f44336' },
    { key: 'clean', label: 'Cleaning', color: '#ffffff' }
  ];

  const itemName = item.name || item;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999
        }}
        onClick={onClose}
        onTouchEnd={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: position.y,
          left: position.x,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          zIndex: 1000,
          minWidth: '150px'
        }}
      >
        <div style={{ padding: '8px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '12px' }}>
          Move "{itemName}" to:
        </div>
        {columns
          .filter(col => col.key !== sourceColumn)
          .map(col => (
            <div
              key={col.key}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '12px',
                borderBottom: '1px solid #f0f0f0'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(col.key);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelect(col.key);
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              {col.label}
            </div>
          ))}
        <div style={{ padding: '8px', borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '12px' }}>
          Set Type:
        </div>
        {types.map(type => (
          <div
            key={type.key}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '12px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: type.color,
              color: type.color === '#ffffff' ? '#000' : '#fff',
              border: type.color === '#ffffff' ? '1px solid #ddd' : 'none'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onTypeSelect(type.key);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onTypeSelect(type.key);
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '1';
            }}
          >
            {type.label} {(item.type === type.key || (!item.type && type.key === null)) && '✓'}
          </div>
        ))}
        <div
          style={{
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#dc3545',
            fontWeight: 'bold',
            backgroundColor: '#fff5f5',
            borderTop: '2px solid #dc3545'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#dc3545';
            e.target.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#fff5f5';
            e.target.style.color = '#dc3545';
          }}
        >
          🗑️ Delete Item
        </div>
      </div>
    </>
  );
};

// Draggable Item Component
const DraggableItem = ({ item, sourceColumn, onMove, onDoubleClick, onLongPress }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'grocery-item',
    item: { item, sourceColumn },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  // Get color based on type
  const getTypeColor = (type) => {
    switch(type) {
      case 'sg': return '#ff9800';
      case 'vegetables': return '#4caf50';
      case 'meat': return '#f44336';
      case 'clean': return '#ffffff';
      default: return '#007bff'; // default blue
    }
  };

  const itemType = item.type || null;
  const backgroundColor = itemType ? getTypeColor(itemType) : '#007bff';
  const textColor = itemType === 'clean' ? '#000' : '#fff';
  const border = itemType === 'clean' ? '1px solid #ddd' : 'none';

  // Handle touch double-tap
  const [lastTap, setLastTap] = React.useState(0);
  
  // Track if user is dragging to prevent click event
  const [isDraggingLocal, setIsDraggingLocal] = React.useState(false);
  const [touchStartPos, setTouchStartPos] = React.useState(null);
  
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setIsDraggingLocal(false);
  };
  
  const handleTouchMove = (e) => {
    if (touchStartPos) {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.y);
      
      // If moved more than 10px, consider it a drag
      if (deltaX > 10 || deltaY > 10) {
        setIsDraggingLocal(true);
      }
    }
  };
  
  const handleTouchEnd = (e) => {
    if (isDraggingLocal) {
      // It was a drag, not a tap
      setIsDraggingLocal(false);
      setTouchStartPos(null);
      return;
    }
    
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    
    if (tapLength < 500 && tapLength > 0) {
      // Double tap detected
      e.preventDefault();
      onDoubleClick(item, sourceColumn);
      setLastTap(0);
    } else {
      // Single tap - show context menu
      const touch = e.changedTouches[0];
      onLongPress(item, sourceColumn, { x: touch.clientX, y: touch.clientY });
      setLastTap(currentTime);
    }
    
    setTouchStartPos(null);
  };

  const handleClick = (e) => {
    // For desktop - show menu on click
    if (!isDragging) {
      onLongPress(item, sourceColumn, { x: e.clientX, y: e.clientY });
    }
  };

  const handleContextMenu = (e) => {
    // Desktop right-click - prevent default and show menu
    e.preventDefault();
    onLongPress(item, sourceColumn, { x: e.clientX, y: e.clientY });
  };

  return (
    <div
      ref={drag}
      className="badge mb-1 mr-1 p-1"
      style={{
        cursor: 'grab',
        display: 'block',
        fontSize: '12px',
        userSelect: 'none',
        opacity: isDragging ? 0.5 : 1,
        wordBreak: 'break-word',
        lineHeight: '1.2',
        backgroundColor: backgroundColor,
        color: textColor,
        border: border
      }}
      onClick={handleClick}
      onDoubleClick={() => onDoubleClick(item, sourceColumn)}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      title={sourceColumn !== 'items' ? 'Double-click/tap to return to Grocery Items. Click for menu.' : 'Click for menu.'}
    >
      {item.name || item}
    </div>
  );
};

// Drop Zone Component
const DropZone = ({ columnKey, children, onDrop }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'grocery-item',
    drop: (item) => onDrop(item, columnKey),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  // Calculate dynamic min height based on number of items
  let minHeight;
  if (columnKey === 'items') {
    // For grocery items column: very compact when empty, grows with items
    minHeight = children.length === 0 ? '50px' : 'auto';
  } else {
    // For store columns: standard height
    minHeight = '300px';
  }
  
  return (
    <div
      ref={drop}
      className="card-body"
      style={{
        backgroundColor: isOver ? '#e3f2fd' : (children.length === 0 ? '#f8f9fa' : 'white'),
        minHeight: minHeight,
        border: isOver ? '2px solid #2196f3' : '2px dashed #dee2e6',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        padding: '8px'
      }}
    >
      {children}
    </div>
  );
};

// Add New Item Button Component
const AddItemButton = ({ onClick }) => {
  return (
    <div
      className="badge badge-success mb-1 mr-1 p-1"
      style={{
        cursor: 'pointer',
        display: 'block',
        fontSize: '12px',
        userSelect: 'none',
        textAlign: 'center',
        minWidth: '25px',
        touchAction: 'manipulation'
      }}
      onClick={onClick}
      title="Click to add a new grocery item"
    >
      +
    </div>
  );
};

// New Item Input Component
const NewItemInput = ({ value, onChange, onSubmit, onCancel }) => {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSubmit(e);
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="mb-1 mr-1" style={{ display: 'block' }}>
      <input
        type="text"
        className="form-control form-control-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={handleKeyPress}
        onBlur={onCancel}
        placeholder="Enter item..."
        autoFocus
        style={{
          fontSize: '12px',
          padding: '2px 4px'
        }}
      />
    </div>
  );
};

class GroceryDragDrop extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      groceryItems: [], // Will be loaded from API
      columns: {
        items: [],
        ametller: [],
        bonpreu: [],
        others: []
      },
      typeFilter: null,
      searchQuery: "",
      itemsExpanded: false,
      isAddingNewItem: false,
      newItemName: "",
      contextMenu: null,
      loading: true,
      error: null,
      familyId: null
    };

    this.handleDrop = this.handleDrop.bind(this);
    this.resetColumns = this.resetColumns.bind(this);
    this.resetColumn = this.resetColumn.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.handleAddNewItem = this.handleAddNewItem.bind(this);
    this.handleNewItemSubmit = this.handleNewItemSubmit.bind(this);
    this.handleNewItemCancel = this.handleNewItemCancel.bind(this);
    this.handleLongPress = this.handleLongPress.bind(this);
    this.handleContextMenuSelect = this.handleContextMenuSelect.bind(this);
    this.handleContextMenuClose = this.handleContextMenuClose.bind(this);
    this.handleDeleteItem = this.handleDeleteItem.bind(this);
    this.handleTypeSelect = this.handleTypeSelect.bind(this);
    this.loadGroceryItems = this.loadGroceryItems.bind(this);
  }

  componentDidMount() {
    const selectedFamily = JSON.parse(localStorage.getItem("selectedFamily"));
    
    if (selectedFamily) {
      this.setState({ familyId: selectedFamily.id }, () => {
        this.loadGroceryItems();
      });
    } else {
      const currentUser = AuthService.getCurrentUser();
      if (currentUser && currentUser.families && currentUser.families.length > 0) {
        const family = currentUser.families[0];
        localStorage.setItem("selectedFamily", JSON.stringify(family));
        this.setState({ familyId: family.id }, () => {
          this.loadGroceryItems();
        });
      } else {
        this.setState({ 
          loading: false, 
          error: "No family found. Please create or join a family first." 
        });
      }
    }
  }

  async loadGroceryItems() {
    try {
      this.setState({ loading: true, error: null });
      const response = await FamilyService.getGroceryItemsByFamilyId(this.state.familyId);
      const items = response.data;
      
      // Organize items by store
      const columns = {
        items: [],
        ametller: [],
        bonpreu: [],
        others: []
      };

      items.forEach(item => {
        const storeName = item.store.toLowerCase();
        if (storeName === 'none') {
          columns.items.push(item);
        } else if (storeName === 'ametller') {
          columns.ametller.push(item);
        } else if (storeName === 'bonpreu') {
          columns.bonpreu.push(item);
        } else if (storeName === 'others') {
          columns.others.push(item);
        }
      });

      this.setState({ 
        groceryItems: items,
        columns,
        loading: false 
      });
    } catch (error) {
      console.error("Error loading grocery items:", error);
      this.setState({ 
        loading: false,
        error: "Failed to load grocery items. Please try again later."
      });
    }
  }

  async handleDrop(draggedItem, targetColumn) {
    const { item, sourceColumn } = draggedItem;

    if (sourceColumn === targetColumn) {
      return; // No need to move if dropping in the same column
    }

    // Map column key to store name
    const storeMap = {
      'items': 'None',
      'ametller': 'Ametller',
      'bonpreu': 'Bonpreu',
      'others': 'Others'
    };

    try {
      // Update item in backend
      await FamilyService.updateGroceryItemInFamily(
        this.state.familyId,
        item._id,
        { store: storeMap[targetColumn] }
      );

      // Update local state
      this.setState(prevState => {
        const newColumns = { ...prevState.columns };
        
        // Remove item from source column
        newColumns[sourceColumn] = newColumns[sourceColumn].filter(i => i._id !== item._id);
        
        // Add item to target column (avoid duplicates)
        const itemExists = newColumns[targetColumn].some(i => i._id === item._id);
        if (!itemExists) {
          const updatedItem = { ...item, store: storeMap[targetColumn] };
          newColumns[targetColumn] = [...newColumns[targetColumn], updatedItem];
        }

        return { columns: newColumns };
      });
    } catch (error) {
      console.error("Error updating grocery item:", error);
      alert("Failed to move item. Please try again.");
    }
  }

  async resetColumns() {
    try {
      // Update all items to store "None"
      const updatePromises = this.state.groceryItems.map(item => 
        FamilyService.updateGroceryItemInFamily(
          this.state.familyId,
          item._id,
          { store: 'None' }
        )
      );
      
      await Promise.all(updatePromises);
      
      // Reload items from backend
      await this.loadGroceryItems();
      
      this.setState({
        isAddingNewItem: false,
        newItemName: ""
      });
    } catch (error) {
      console.error("Error resetting columns:", error);
      alert("Failed to reset columns. Please try again.");
    }
  }

  async resetColumn(columnKey) {
    if (columnKey === 'items') return; // Don't reset the main items column
    
    try {
      const itemsToReturn = [...this.state.columns[columnKey]];
      
      // Update all items in this column to store "None"
      const updatePromises = itemsToReturn.map(item => 
        FamilyService.updateGroceryItemInFamily(
          this.state.familyId,
          item._id,
          { store: 'None' }
        )
      );
      
      await Promise.all(updatePromises);
      
      // Update local state
      this.setState(prevState => {
        const newColumns = { ...prevState.columns };
        const itemsWithUpdatedStore = itemsToReturn.map(item => ({ ...item, store: 'None' }));
        
        // Move all items from the specified column back to items
        newColumns.items = [...newColumns.items, ...itemsWithUpdatedStore];
        newColumns[columnKey] = [];
        
        return { columns: newColumns };
      });
    } catch (error) {
      console.error("Error resetting column:", error);
      alert("Failed to reset column. Please try again.");
    }
  }

  async handleDoubleClick(item, sourceColumn) {
    if (sourceColumn === 'items') return; // Already in the initial column
    
    try {
      // Update item to store "None"
      await FamilyService.updateGroceryItemInFamily(
        this.state.familyId,
        item._id,
        { store: 'None' }
      );

      // Update local state
      this.setState(prevState => {
        const newColumns = { ...prevState.columns };
        
        // Remove item from source column
        newColumns[sourceColumn] = newColumns[sourceColumn].filter(i => i._id !== item._id);
        
        // Add item back to items column (avoid duplicates)
        const itemExists = newColumns.items.some(i => i._id === item._id);
        if (!itemExists) {
          const updatedItem = { ...item, store: 'None' };
          newColumns.items = [...newColumns.items, updatedItem];
        }
        
        return { columns: newColumns };
      });
    } catch (error) {
      console.error("Error moving item back:", error);
      alert("Failed to move item. Please try again.");
    }
  }

  handleAddNewItem() {
    this.setState({ 
      isAddingNewItem: true,
      newItemName: ""
    });
  }

  async handleNewItemSubmit(e) {
    e.preventDefault();
    const trimmedName = this.state.newItemName.trim();
    
    if (trimmedName) {
      // Check for duplicates
      const isDuplicate = this.state.groceryItems.some(
        item => item.name.toLowerCase() === trimmedName.toLowerCase()
      );
      
      if (isDuplicate) {
        alert("This item already exists!");
        this.handleNewItemCancel();
        return;
      }

      try {
        // Add item to backend
        const response = await FamilyService.addGroceryItemToFamily(
          this.state.familyId,
          { name: trimmedName, store: 'None' }
        );
        
        const newItem = response.data;

        // Update local state
        this.setState(prevState => ({
          columns: {
            ...prevState.columns,
            items: [...prevState.columns.items, newItem]
          },
          groceryItems: [...prevState.groceryItems, newItem],
          isAddingNewItem: false,
          newItemName: ""
        }));
      } catch (error) {
        console.error("Error adding grocery item:", error);
        alert("Failed to add item. Please try again.");
        this.handleNewItemCancel();
      }
    } else {
      // If empty, just cancel
      this.handleNewItemCancel();
    }
  }

  handleNewItemCancel() {
    this.setState({
      isAddingNewItem: false,
      newItemName: ""
    });
  }

  handleLongPress(item, sourceColumn, position) {
    this.setState({
      contextMenu: {
        item,
        sourceColumn,
        position
      }
    });
  }

  handleContextMenuSelect(targetColumn) {
    if (!this.state.contextMenu) return;
    
    const { item, sourceColumn } = this.state.contextMenu;
    
    // Move item to selected column
    this.handleDrop({ item, sourceColumn }, targetColumn);
    
    // Close menu
    this.handleContextMenuClose();
  }

  handleContextMenuClose() {
    this.setState({ contextMenu: null });
  }

  async handleTypeSelect(selectedType) {
    if (!this.state.contextMenu) return;
    
    const { item, sourceColumn } = this.state.contextMenu;
    
    try {
      // Update item type in backend
      await FamilyService.updateGroceryItemInFamily(
        this.state.familyId,
        item._id,
        { type: selectedType }
      );

      // Update local state
      this.setState(prevState => {
        const newColumns = { ...prevState.columns };
        
        // Update the item in the current column
        newColumns[sourceColumn] = newColumns[sourceColumn].map(i => 
          i._id === item._id ? { ...i, type: selectedType } : i
        );
        
        // Update in groceryItems master list
        const newGroceryItems = prevState.groceryItems.map(i => 
          i._id === item._id ? { ...i, type: selectedType } : i
        );
        
        return {
          columns: newColumns,
          groceryItems: newGroceryItems,
          contextMenu: null
        };
      });
    } catch (error) {
      console.error("Error updating item type:", error);
      alert("Failed to update item type. Please try again.");
      this.handleContextMenuClose();
    }
  }

  async handleDeleteItem() {
    if (!this.state.contextMenu) return;
    
    const { item, sourceColumn } = this.state.contextMenu;
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      this.handleContextMenuClose();
      return;
    }

    try {
      // Delete item from backend
      await FamilyService.removeGroceryItemFromFamily(
        this.state.familyId,
        item._id
      );

      // Update local state
      this.setState(prevState => {
        const newColumns = { ...prevState.columns };
        
        // Remove item from the source column
        newColumns[sourceColumn] = newColumns[sourceColumn].filter(i => i._id !== item._id);
        
        // Also remove from all other columns to ensure it's completely deleted
        Object.keys(newColumns).forEach(key => {
          newColumns[key] = newColumns[key].filter(i => i._id !== item._id);
        });
        
        // Remove from groceryItems master list
        const newGroceryItems = prevState.groceryItems.filter(i => i._id !== item._id);
        
        return {
          columns: newColumns,
          groceryItems: newGroceryItems,
          contextMenu: null
        };
      });
    } catch (error) {
      console.error("Error deleting grocery item:", error);
      alert("Failed to delete item. Please try again.");
      this.handleContextMenuClose();
    }
  }

  renderGroceryItemsColumn() {
    const items = this.state.columns.items;
    const { typeFilter, searchQuery, itemsExpanded } = this.state;
    const isSearching = searchQuery.trim() !== '';
    const showItemsList = itemsExpanded || isSearching;
    const types = [
      { key: null, label: 'All', color: '#6c757d' },
      { key: 'none', label: 'No Type', color: '#007bff' },
      { key: 'sg', label: 'SG', color: '#ff9800' },
      { key: 'vegetables', label: 'Vegetables', color: '#4caf50' },
      { key: 'meat', label: 'Meat', color: '#f44336' },
      { key: 'clean', label: 'Cleaning', color: '#adb5bd' }
    ];

    // Sort items alphabetically by name
    const sortedItems = [...items]
      .filter(item => {
        const matchesType = typeFilter === null
          ? true
          : typeFilter === 'none'
            ? !item.type
            : item.type === typeFilter;
        const matchesSearch = searchQuery.trim() === ''
          ? true
          : (item.name || item).toString().toLowerCase().includes(searchQuery.trim().toLowerCase());
        return matchesType && matchesSearch;
      })
      .sort((a, b) => {
        const nameA = (a.name || a).toString().toLowerCase();
        const nameB = (b.name || b).toString().toLowerCase();
        return nameA.localeCompare(nameB);
      });
    
    return (
      <div className="col-12 mb-3">
        <div className="card">
          <div className="card-header">
            <div
              className="d-flex justify-content-between align-items-center mb-1"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => this.setState(prev => ({ itemsExpanded: !prev.itemsExpanded }))}
              role="button"
              aria-expanded={itemsExpanded}
              title={itemsExpanded ? 'Click to collapse' : 'Click to expand'}
            >
              <h6 className="card-title mb-0" style={{ fontSize: '16px' }}>
                Grocery Items ({items.length})
              </h6>
              <span style={{ fontSize: '12px' }}>{itemsExpanded ? '▲' : '▼'}</span>
            </div>
            <div style={{ position: 'relative' }} className="mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => this.setState({ searchQuery: e.target.value })}
                style={{ fontSize: '12px', paddingRight: '28px' }}
              />
              {searchQuery !== '' && (
                <button
                  type="button"
                  aria-label="Clear search"
                  title="Clear search"
                  onClick={() => this.setState({ searchQuery: '' })}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '6px',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: 1,
                    padding: '2px 4px',
                    color: '#6c757d'
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
              {types.map(type => {
                const isActive = typeFilter === type.key;
                return (
                  <button
                    key={String(type.key)}
                    onClick={() => this.setState({ typeFilter: type.key })}
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      border: `1px solid ${type.color}`,
                      backgroundColor: isActive ? type.color : 'transparent',
                      color: isActive ? (type.color === '#adb5bd' ? '#000' : '#fff') : type.color,
                      cursor: 'pointer',
                      fontWeight: isActive ? 'bold' : 'normal',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {type.label}
                    {type.key !== null && ` (${items.filter(i => type.key === 'none' ? !i.type : i.type === type.key).length})`}
                  </button>
                );
              })}
            </div>
          </div>
          {showItemsList && (
          <DropZone 
            columnKey="items" 
            onDrop={this.handleDrop}
            children={items}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
              {items.length === 0 && !this.state.isAddingNewItem && (
                <p className="text-muted text-center w-100" style={{ margin: '4px 0', fontSize: '11px' }}>
                  Click + to add items
                </p>
              )}
              {items.length > 0 && sortedItems.length === 0 && (
                <p className="text-muted text-center w-100" style={{ margin: '4px 0', fontSize: '11px' }}>
                  No items match this filter
                </p>
              )}
              {sortedItems.map((item, index) => (
                <DraggableItem
                  key={`items-${item}-${index}`}
                  item={item}
                  sourceColumn="items"
                  onMove={this.handleDrop}
                  onDoubleClick={this.handleDoubleClick}
                  onLongPress={this.handleLongPress}
                />
              ))}
              {this.state.isAddingNewItem ? (
                <NewItemInput
                  value={this.state.newItemName}
                  onChange={(value) => this.setState({ newItemName: value })}
                  onSubmit={this.handleNewItemSubmit}
                  onCancel={this.handleNewItemCancel}
                />
              ) : (
                <AddItemButton onClick={this.handleAddNewItem} />
              )}
            </div>
          </DropZone>
          )}
        </div>
      </div>
    );
  }

  renderStoreColumn(columnKey, columnTitle) {
    const items = this.state.columns[columnKey];
    // Sort items alphabetically by name
    const sortedItems = [...items].sort((a, b) => {
      const nameA = (a.name || a).toString().toLowerCase();
      const nameB = (b.name || b).toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });
    const showResetButton = items.length > 0;
    
    return (
      <div 
        style={{ 
          minWidth: '140px',
          flex: '1 1 140px',
          marginBottom: '1rem'
        }}
      >
        <div className="card h-100">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="card-title mb-0" style={{ fontSize: '14px' }}>{columnTitle}</h6>
            {showResetButton && (
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={() => this.resetColumn(columnKey)}
                title={`Return all items from ${columnTitle} to Grocery Items`}
                style={{ fontSize: '10px', padding: '1px 4px' }}
              >
                ↺
              </button>
            )}
          </div>
          <DropZone 
            columnKey={columnKey} 
            onDrop={this.handleDrop}
            children={items}
          >
            {items.length === 0 ? (
              <p className="text-muted text-center" style={{ marginTop: '30px', fontSize: '12px' }}>
                Drop items here
              </p>
            ) : (
              <div>
                {sortedItems.map((item, index) => (
                  <DraggableItem
                    key={`${columnKey}-${item}-${index}`}
                    item={item}
                    sourceColumn={columnKey}
                    onMove={this.handleDrop}
                    onDoubleClick={this.handleDoubleClick}
                    onLongPress={this.handleLongPress}
                  />
                ))}
              </div>
            )}
          </DropZone>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="container-fluid">
          <div className="card">
            <div className="card-body text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading grocery items...</p>
            </div>
          </div>
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="alert alert-danger" role="alert">
                {this.state.error}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <DndProvider backend={getBackend()}>
        <div className="container-fluid">
          {this.state.contextMenu && (
            <ContextMenu
              item={this.state.contextMenu.item}
              sourceColumn={this.state.contextMenu.sourceColumn}
              position={this.state.contextMenu.position}
              onSelect={this.handleContextMenuSelect}
              onClose={this.handleContextMenuClose}
              onDelete={this.handleDeleteItem}
              onTypeSelect={this.handleTypeSelect}
            />
          )}
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Grocery Store Organizer</h5>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={this.resetColumns}
                >
                  Reset
                </button>
              </div>
              
              {/* Summary Section */}
              <div className="row mb-3">
                <div className="col-12">
                  <h6 style={{ fontSize: '14px' }}>Summary:</h6>
                  <div className="d-flex justify-content-around">
                    <small className="text-muted" style={{ fontSize: '12px' }}>
                      <strong>Grocery Items:</strong> {this.state.columns.items.length}
                    </small>
                    <small className="text-muted" style={{ fontSize: '12px' }}>
                      <strong>Ametller:</strong> {this.state.columns.ametller.length}
                    </small>
                    <small className="text-muted" style={{ fontSize: '12px' }}>
                      <strong>BonPreu:</strong> {this.state.columns.bonpreu.length}
                    </small>
                    <small className="text-muted" style={{ fontSize: '12px' }}>
                      <strong>Others:</strong> {this.state.columns.others.length}
                    </small>
                  </div>
                </div>
              </div>

              {/* Grocery Items Row */}
              <div className="row">
                {this.renderGroceryItemsColumn()}
              </div>
              
              {/* Store Columns Row */}
              <div 
                style={{ 
                  display: 'flex',
                  gap: '1rem',
                  overflowX: 'auto',
                  paddingBottom: '1rem',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {this.renderStoreColumn('ametller', 'Ametller')}
                {this.renderStoreColumn('bonpreu', 'BonPreu')}
                {this.renderStoreColumn('others', 'Others')}
              </div>


            </div>
          </div>
        </div>
      </DndProvider>
    );
  }
}

export default GroceryDragDrop;
